/**
 * "At a glance" summary: 4 stat cards + a segmented accepted/rejected/
 * undecided progress bar with a legend. Mirrors
 * apps/mockups/.../summary.mockup.tsx, adapted to real i18n strings and the
 * real UnitDecisionsSummary shape.
 */
import { CheckCircle2, XCircle, Clock, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { StatCard } from '@ooh/ui/components/stat-card';
import { InfoHint } from '@ooh/ui/components/info-hint';
import type { UnitDecisionsSummary } from './filters';
import { formatOfferedPrice } from './format-helpers';

export interface UnitDecisionsSummaryPanelProps {
  summary: UnitDecisionsSummary;
}

export function UnitDecisionsSummaryPanel({ summary }: UnitDecisionsSummaryPanelProps) {
  const { t } = useTranslation('offers');

  if (summary.total === 0) return null;

  const pctAccepted = Math.round((summary.accepted / summary.total) * 100);
  const pctRejected = Math.round((summary.rejected / summary.total) * 100);
  const pctUndecided = 100 - pctAccepted - pctRejected;

  const acceptedLabel = (
    <span className="flex items-center gap-1">
      {t('workspace.versions.unitDecisions.summary.acceptedLabel')}
      {summary.silentCount > 0 ? (
        <InfoHint label={t('workspace.versions.unitDecisions.summary.silentHintAriaLabel')}>
          {t('workspace.versions.unitDecisions.summary.silentHint', {
            count: summary.silentCount,
          })}
        </InfoHint>
      ) : null}
    </span>
  );

  return (
    <div className="space-y-3" data-testid="unit-decisions-summary">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={CheckCircle2}
          label={acceptedLabel}
          value={summary.accepted}
          loading={false}
          tone="success"
          testId="unit-decisions-stat-accepted"
        />
        <StatCard
          icon={XCircle}
          label={t('workspace.versions.unitDecisions.summary.rejectedLabel')}
          value={summary.rejected}
          loading={false}
          tone="destructive"
          testId="unit-decisions-stat-rejected"
        />
        <StatCard
          icon={Clock}
          label={t('workspace.versions.unitDecisions.summary.undecidedLabel')}
          value={summary.undecided}
          loading={false}
          testId="unit-decisions-stat-undecided"
        />
        <StatCard
          icon={Wallet}
          label={t('workspace.versions.unitDecisions.summary.acceptedValueLabel')}
          value={formatOfferedPrice(summary.acceptedValuePln)}
          loading={false}
          testId="unit-decisions-stat-accepted-value"
        />
      </div>

      <div>
        <div className="flex h-2 overflow-hidden rounded-full bg-muted">
          <div className="bg-emerald-500" style={{ width: `${pctAccepted}%` }} />
          <div className="bg-red-500" style={{ width: `${pctRejected}%` }} />
          <div className="bg-muted-foreground/30" style={{ width: `${pctUndecided}%` }} />
        </div>
        <div className="mt-1.5 flex gap-4 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-emerald-500" />
            {t('workspace.versions.unitDecisions.summary.legendAccepted', {
              count: summary.accepted,
              pct: pctAccepted,
            })}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-red-500" />
            {t('workspace.versions.unitDecisions.summary.legendRejected', {
              count: summary.rejected,
              pct: pctRejected,
            })}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-muted-foreground/30" />
            {t('workspace.versions.unitDecisions.summary.legendUndecided', {
              count: summary.undecided,
              pct: pctUndecided,
            })}
          </span>
        </div>
      </div>
    </div>
  );
}
